// Utility functions for PDF page operations

export const PAGE_REMOVAL_TYPES = {
  FIRST: 'first',
  LAST: 'last',
  FIRST_N: 'first-n',
  LAST_N: 'last-n',
  RANGE: 'range',
  MIDDLE_N: 'middle-n',
  CUSTOM: 'custom',
  KEEP: 'keep'
};

export const validatePageNumbers = (pageNumbers, totalPages) => {
  if (!Array.isArray(pageNumbers)) return false;
  if (pageNumbers.length === 0) return false;

  return pageNumbers.every(page =>
    Number.isInteger(page) && page >= 1 && page <= totalPages
  );
};

export const validateRemovalConfig = (config, totalPages) => {
  const { type, options = {} } = config;

  switch (type) {
    case PAGE_REMOVAL_TYPES.FIRST:
    case PAGE_REMOVAL_TYPES.LAST:
      return totalPages > 1; // Can't remove if only 1 page

    case PAGE_REMOVAL_TYPES.FIRST_N:
    case PAGE_REMOVAL_TYPES.LAST_N:
    case PAGE_REMOVAL_TYPES.MIDDLE_N:
      const count = options.count || 1;
      return count > 0 && count < totalPages; // Must leave at least 1 page

    case PAGE_REMOVAL_TYPES.RANGE:
      const { startPage = 1, endPage = totalPages } = options;
      const validRange = startPage >= 1 &&
                        endPage <= totalPages &&
                        startPage <= endPage;
      const wouldRemoveAll = startPage === 1 && endPage === totalPages;
      return validRange && !wouldRemoveAll;

    case PAGE_REMOVAL_TYPES.CUSTOM:
      return validatePageNumbers(config.pagesToRemove, totalPages) &&
             config.pagesToRemove.length < totalPages;

    case PAGE_REMOVAL_TYPES.KEEP:
      return validatePageNumbers(config.pagesToKeep, totalPages) &&
             config.pagesToKeep.length > 0;

    default:
      return false;
  }
};

export const formatPageRange = (pages) => {
  if (!Array.isArray(pages) || pages.length === 0) return '';

  const sorted = [...pages].sort((a, b) => a - b);
  const ranges = [];
  let start = sorted[0];
  let end = sorted[0];

  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] === end + 1) {
      end = sorted[i];
    } else {
      ranges.push(start === end ? `${start}` : `${start}-${end}`);
      start = end = sorted[i];
    }
  }

  ranges.push(start === end ? `${start}` : `${start}-${end}`);
  return ranges.join(', ');
};

export const parsePageRange = (rangeString, totalPages) => {
  if (!rangeString || typeof rangeString !== 'string') return [];

  const pages = new Set();
  const parts = rangeString.split(',').map(part => part.trim());

  for (const part of parts) {
    if (part.includes('-')) {
      const [startStr, endStr] = part.split('-').map(s => s.trim());
      const start = parseInt(startStr);
      const end = parseInt(endStr);

      if (!isNaN(start) && !isNaN(end) && start <= end) {
        for (let i = Math.max(1, start); i <= Math.min(totalPages, end); i++) {
          pages.add(i);
        }
      }
    } else {
      const pageNum = parseInt(part);
      if (!isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPages) {
        pages.add(pageNum);
      }
    }
  }

  return Array.from(pages).sort((a, b) => a - b);
};

export const getRemovalDescription = (config, totalPages) => {
  const { type, options = {} } = config;

  switch (type) {
    case PAGE_REMOVAL_TYPES.FIRST:
      return 'Remove first page';

    case PAGE_REMOVAL_TYPES.LAST:
      return 'Remove last page';

    case PAGE_REMOVAL_TYPES.FIRST_N:
      const firstCount = options.count || 1;
      return `Remove first ${firstCount} page${firstCount > 1 ? 's' : ''}`;

    case PAGE_REMOVAL_TYPES.LAST_N:
      const lastCount = options.count || 1;
      return `Remove last ${lastCount} page${lastCount > 1 ? 's' : ''}`;

    case PAGE_REMOVAL_TYPES.MIDDLE_N:
      const midCount = options.count || 1;
      return `Remove ${midCount} page${midCount > 1 ? 's' : ''} from middle`;

    case PAGE_REMOVAL_TYPES.RANGE:
      const { startPage = 1, endPage = totalPages } = options;
      return `Remove pages ${startPage}-${endPage}`;

    case PAGE_REMOVAL_TYPES.CUSTOM:
      if (config.pagesToRemove?.length) {
        const formatted = formatPageRange(config.pagesToRemove);
        return `Remove pages: ${formatted}`;
      }
      return 'Remove selected pages';

    case PAGE_REMOVAL_TYPES.KEEP:
      if (config.pagesToKeep?.length) {
        const formatted = formatPageRange(config.pagesToKeep);
        return `Keep only pages: ${formatted}`;
      }
      return 'Keep selected pages only';

    default:
      return 'Unknown operation';
  }
};

export const estimateProcessingTime = (totalPages, removalCount) => {
  // Rough estimates in milliseconds
  const baseTime = 500; // Base processing time
  const perPageTime = 50; // Time per page to process
  const removalTime = removalCount * 20; // Time per page to remove

  return baseTime + (totalPages * perPageTime) + removalTime;
};

export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const generatePreviewPages = (totalPages, removalConfig) => {
  const allPages = Array.from({ length: totalPages }, (_, i) => ({
    pageNumber: i + 1,
    willBeRemoved: false,
    willBeKept: true
  }));

  let pagesToRemove = [];

  if (removalConfig.type === PAGE_REMOVAL_TYPES.KEEP && removalConfig.pagesToKeep) {
    // For 'keep' mode, mark pages NOT in the keep list as removed
    const keepSet = new Set(removalConfig.pagesToKeep);
    pagesToRemove = allPages
      .filter(page => !keepSet.has(page.pageNumber))
      .map(page => page.pageNumber);
  } else if (removalConfig.type === PAGE_REMOVAL_TYPES.CUSTOM && removalConfig.pagesToRemove) {
    pagesToRemove = removalConfig.pagesToRemove;
  } else {
    // For other types, calculate which pages would be removed
    pagesToRemove = getPageNumbersToRemove(totalPages, removalConfig.type, removalConfig.options);
  }

  const removeSet = new Set(pagesToRemove);
  return allPages.map(page => ({
    ...page,
    willBeRemoved: removeSet.has(page.pageNumber),
    willBeKept: !removeSet.has(page.pageNumber)
  }));
};

// Helper function to get pages to remove (moved from service to avoid circular dependency)
const getPageNumbersToRemove = (totalPages, removalType, options = {}) => {
  const { count = 1, startPage = 1, endPage = totalPages } = options;

  switch (removalType) {
    case PAGE_REMOVAL_TYPES.FIRST:
      return [1];

    case PAGE_REMOVAL_TYPES.LAST:
      return [totalPages];

    case PAGE_REMOVAL_TYPES.FIRST_N:
      return Array.from({ length: Math.min(count, totalPages - 1) }, (_, i) => i + 1);

    case PAGE_REMOVAL_TYPES.LAST_N:
      const startIdx = Math.max(1, totalPages - count + 1);
      return Array.from({ length: totalPages - startIdx + 1 }, (_, i) => startIdx + i);

    case PAGE_REMOVAL_TYPES.RANGE:
      const start = Math.max(1, Math.min(startPage, totalPages));
      const end = Math.min(totalPages, Math.max(startPage, endPage));
      return Array.from({ length: end - start + 1 }, (_, i) => start + i);

    case PAGE_REMOVAL_TYPES.MIDDLE_N:
      const middleStart = Math.max(1, Math.floor((totalPages - count) / 2) + 1);
      const middleEnd = Math.min(totalPages, middleStart + count - 1);
      return Array.from({ length: middleEnd - middleStart + 1 }, (_, i) => middleStart + i);

    default:
      return [];
  }
};

export const DEFAULT_REMOVAL_OPTIONS = {
  [PAGE_REMOVAL_TYPES.RANGE]: { startPage: 1, endPage: 1 },
  [PAGE_REMOVAL_TYPES.CUSTOM]: { pagesToRemove: [] },
  [PAGE_REMOVAL_TYPES.KEEP]: { pagesToKeep: [] }
};